// src/views/Admin/Permissions/Permissions.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Dropdown } from 'react-bootstrap';
import {
  HiOutlineShieldCheck,
  HiOutlinePlus,
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineCurrencyDollar,
  HiOutlineCheck,
  HiOutlineXMark
} from 'react-icons/hi2';
import { FiSearch } from 'react-icons/fi';
import { toast } from 'react-toastify';
import {
  GetAllApplicationPermissions,
  CreateApplicationPermission,
  DeleteApplicationPermission,
  UpdateApplicationPermissionType,
  UpdateApplicationPermissionName,
  UpdateApplicationPermissionDescription,
  UpdateApplicationPermissionCategory,
  ToggleApplicationPermissionAffectMoney
} from '../../../crud/configuration_api/application_permissions';
import MiniLoadingSpinner from '../../../components/Spinners/MiniLoadingSpinner';
import './Permissions.scss';

// Types
interface Permission {
  application_permission_id: number;
  application_permission_name: string;
  application_permission_description: string | null;
  affect_money: boolean;
  category: string;
  subcategory: string | null;
  permission_type: string | null;
  timestamp: string;
}

interface GroupedPermissions {
  [category: string]: {
    [subcategory: string]: Permission[];
  };
}

// Permission type options in Spanish
const PERMISSION_TYPES = [
  { value: 'read', label: 'Leer' },
  { value: 'create', label: 'Crear' },
  { value: 'update', label: 'Actualizar' },
  { value: 'delete', label: 'Eliminar' }
];

const Permissions: React.FC = () => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedSubcategories, setExpandedSubcategories] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPermission, setEditingPermission] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<'name' | 'description' | null>(null);
  const [editValue, setEditValue] = useState('');

  // Fetch permissions on mount
  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const response = await GetAllApplicationPermissions();
      if (response.status === 200) {
        setPermissions(response.data);
        // Expand all categories by default
        const categories = new Set<string>(response.data.map((p: Permission) => p.category));
        setExpandedCategories(categories);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      toast.error('Error al cargar los permisos');
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories and subcategories for suggestions
  const { categories, subcategories } = useMemo(() => {
    const cats = [...new Set(permissions.map(p => p.category).filter(Boolean))];
    const subs = [...new Set(permissions.map(p => p.subcategory).filter(Boolean))] as string[];
    return { categories: cats, subcategories: subs };
  }, [permissions]);

  // Group permissions by category, then by subcategory within each category
  const groupedPermissions = useMemo(() => {
    let filtered = permissions.filter(p =>
      p.application_permission_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.application_permission_description?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      p.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.subcategory?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Apply type filter
    if (typeFilter !== 'all') {
      if (typeFilter === 'none') {
        filtered = filtered.filter(p => !p.permission_type);
      } else {
        filtered = filtered.filter(p => p.permission_type === typeFilter);
      }
    }

    // First, group by category
    const byCategory: { [category: string]: Permission[] } = {};
    filtered.forEach(permission => {
      const category = permission.category || 'Sin categoría';
      if (!byCategory[category]) {
        byCategory[category] = [];
      }
      byCategory[category].push(permission);
    });

    // Then, within each category, group by subcategory
    const grouped: GroupedPermissions = {};
    Object.entries(byCategory).forEach(([category, perms]) => {
      // Sort permissions by subcategory to ensure same subcategories are together
      const sortedPerms = [...perms].sort((a, b) => {
        const subA = a.subcategory || 'General';
        const subB = b.subcategory || 'General';
        return subA.localeCompare(subB);
      });

      grouped[category] = {};
      sortedPerms.forEach(permission => {
        const subcategory = permission.subcategory || 'General';
        if (!grouped[category][subcategory]) {
          grouped[category][subcategory] = [];
        }
        grouped[category][subcategory].push(permission);
      });
    });

    return grouped;
  }, [permissions, searchTerm, typeFilter]);

  // Toggle category expansion
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Toggle subcategory expansion
  const toggleSubcategory = (key: string) => {
    setExpandedSubcategories(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Handle permission type change
  const handleTypeChange = async (permissionId: number, newType: string) => {
    try {
      const response = await UpdateApplicationPermissionType(permissionId, newType);
      if (response.status === 200) {
        setPermissions(prev =>
          prev.map(p =>
            p.application_permission_id === permissionId
              ? { ...p, permission_type: newType }
              : p
          )
        );
        toast.success('Tipo de permiso actualizado');
      }
    } catch (error) {
      console.error('Error updating permission type:', error);
      toast.error('Error al actualizar el tipo de permiso');
    }
  };

  // Handle affect_money toggle
  const handleToggleAffectMoney = async (permissionId: number) => {
    try {
      const response = await ToggleApplicationPermissionAffectMoney(permissionId);
      if (response.status === 200) {
        setPermissions(prev =>
          prev.map(p =>
            p.application_permission_id === permissionId
              ? { ...p, affect_money: !p.affect_money }
              : p
          )
        );
        toast.success('Estado de "Afecta dinero" actualizado');
      }
    } catch (error) {
      console.error('Error toggling affect_money:', error);
      toast.error('Error al actualizar el estado');
    }
  };

  // Start editing name
  const startEditingName = (permission: Permission) => {
    setEditingPermission(permission.application_permission_id);
    setEditingField('name');
    setEditValue(permission.application_permission_name);
  };

  // Start editing description
  const startEditingDescription = (permission: Permission) => {
    setEditingPermission(permission.application_permission_id);
    setEditingField('description');
    setEditValue(permission.application_permission_description || '');
  };

  // Save edited name
  const saveEditedName = async (permissionId: number) => {
    if (!editValue.trim()) {
      toast.error('El nombre no puede estar vacío');
      return;
    }
    try {
      const response = await UpdateApplicationPermissionName(permissionId, editValue.trim());
      if (response.status === 200) {
        setPermissions(prev =>
          prev.map(p =>
            p.application_permission_id === permissionId
              ? { ...p, application_permission_name: editValue.trim() }
              : p
          )
        );
        toast.success('Nombre actualizado');
      }
    } catch (error: any) {
      console.error('Error updating name:', error);
      toast.error(error.response?.data?.message || 'Error al actualizar el nombre');
    } finally {
      setEditingPermission(null);
      setEditingField(null);
      setEditValue('');
    }
  };

  // Save edited description
  const saveEditedDescription = async (permissionId: number) => {
    try {
      const response = await UpdateApplicationPermissionDescription(permissionId, editValue.trim());
      if (response.status === 200) {
        setPermissions(prev =>
          prev.map(p =>
            p.application_permission_id === permissionId
              ? { ...p, application_permission_description: editValue.trim() || null }
              : p
          )
        );
        toast.success('Descripción actualizada');
      }
    } catch (error: any) {
      console.error('Error updating description:', error);
      toast.error(error.response?.data?.message || 'Error al actualizar la descripción');
    } finally {
      setEditingPermission(null);
      setEditingField(null);
      setEditValue('');
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingPermission(null);
    setEditingField(null);
    setEditValue('');
  };

  // Delete permission
  const handleDeletePermission = async (permissionId: number, permissionName: string) => {
    if (!window.confirm(`¿Estás seguro de eliminar el permiso "${permissionName}"?`)) {
      return;
    }
    try {
      const response = await DeleteApplicationPermission(permissionId);
      if (response.status === 200) {
        setPermissions(prev => prev.filter(p => p.application_permission_id !== permissionId));
        toast.success('Permiso eliminado');
      }
    } catch (error) {
      console.error('Error deleting permission:', error);
      toast.error('Error al eliminar el permiso');
    }
  };

  // Get permission type label in Spanish
  const getTypeLabel = (type: string | null) => {
    if (!type) return 'Sin tipo';
    const found = PERMISSION_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };

  // Count permissions per category
  const getCategoryCount = (category: string) => {
    return Object.values(groupedPermissions[category] || {}).reduce(
      (sum, perms) => sum + perms.length,
      0
    );
  };

  if (loading) {
    return (
      <div className="permissions-page loading-state">
        <MiniLoadingSpinner />
        <p>Cargando permisos...</p>
      </div>
    );
  }

  return (
    <div className="permissions-page">
      {/* Header */}
      <div className="permissions-header">
        <div className="header-title">
          <HiOutlineShieldCheck size={28} />
          <div>
            <h1>Permisos de Aplicación</h1>
            <p>{permissions.length} permisos configurados</p>
          </div>
        </div>
        <button className="create-btn" onClick={() => setShowCreateModal(true)}>
          <HiOutlinePlus size={20} />
          <span>Nuevo Permiso</span>
        </button>
      </div>

      {/* Search & Filters */}
      <div className="permissions-filters">
        <div className="permissions-search">
          <FiSearch size={18} />
          <input
            type="text"
            placeholder="Buscar permisos por nombre, descripción o categoría..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="type-filter">
          <Dropdown className="type-dropdown-container">
            <Dropdown.Toggle
              variant="none"
              className="type-dropdown"
              id="type-filter-dropdown"
            >
              {typeFilter === 'all' ? 'Todos los tipos' : typeFilter === 'none' ? 'Sin tipo' : PERMISSION_TYPES.find(t => t.value === typeFilter)?.label || 'Filtrar por tipo'}
            </Dropdown.Toggle>
            <Dropdown.Menu className="type-dropdown-menu">
              <Dropdown.Item
                onClick={() => setTypeFilter('all')}
                active={typeFilter === 'all'}
              >
                Todos los tipos
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => setTypeFilter('none')}
                active={typeFilter === 'none'}
              >
                Sin tipo
              </Dropdown.Item>
              {PERMISSION_TYPES.map(type => (
                <Dropdown.Item
                  key={type.value}
                  onClick={() => setTypeFilter(type.value)}
                  active={typeFilter === type.value}
                >
                  {type.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* Permissions List */}
      <div className="permissions-list">
        {Object.keys(groupedPermissions).length === 0 ? (
          <div className="no-permissions">
            <HiOutlineShieldCheck size={48} />
            <p>No se encontraron permisos</p>
          </div>
        ) : (
          Object.entries(groupedPermissions).map(([category, subcats]) => (
            <div key={category} className="category-group">
              <div
                className="category-header"
                onClick={() => toggleCategory(category)}
              >
                {expandedCategories.has(category) ? (
                  <HiOutlineChevronDown size={20} />
                ) : (
                  <HiOutlineChevronRight size={20} />
                )}
                <span className="category-name">{category}</span>
                <span className="category-count">{getCategoryCount(category)}</span>
              </div>

              {expandedCategories.has(category) && (
                <div className="category-content">
                  {Object.entries(subcats).map(([subcategory, perms]) => {
                    const subKey = `${category}-${subcategory}`;
                    const isExpanded = expandedSubcategories.has(subKey) || subcategory === 'General';
                    
                    return (
                      <div key={subKey} className="subcategory-group">
                        <div
                          className="subcategory-header"
                          onClick={() => toggleSubcategory(subKey)}
                        >
                          {isExpanded ? (
                            <HiOutlineChevronDown size={16} />
                          ) : (
                            <HiOutlineChevronRight size={16} />
                          )}
                          <span className="subcategory-name">{subcategory}</span>
                          <span className="subcategory-count">{perms.length}</span>
                        </div>

                        {isExpanded && (
                          <div className="permissions-items">
                            {perms.map(permission => (
                              <div key={permission.application_permission_id} className="permission-item">
                                <div className="permission-main">
                                  {/* Permission ID Badge */}
                                  <div className="permission-id-badge" title="ID del permiso para uso en código">
                                    <code>{permission.application_permission_id}</code>
                                  </div>

                                  {/* Name & Description */}
                                  <div className="permission-name-section">
                                    {editingPermission === permission.application_permission_id && editingField === 'name' ? (
                                      <div className="edit-inline">
                                        <input
                                          type="text"
                                          value={editValue}
                                          onChange={(e) => setEditValue(e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') saveEditedName(permission.application_permission_id);
                                            if (e.key === 'Escape') cancelEditing();
                                          }}
                                          autoFocus
                                        />
                                        <button className="save-btn" onClick={() => saveEditedName(permission.application_permission_id)}>
                                          <HiOutlineCheck size={16} />
                                        </button>
                                        <button className="cancel-btn" onClick={cancelEditing}>
                                          <HiOutlineXMark size={16} />
                                        </button>
                                      </div>
                                    ) : (
                                      <span 
                                        className="permission-name clickable" 
                                        onClick={() => startEditingName(permission)}
                                        title="Click para editar nombre"
                                      >
                                        {permission.application_permission_name}
                                      </span>
                                    )}
                                    
                                    {editingPermission === permission.application_permission_id && editingField === 'description' ? (
                                      <div className="edit-inline edit-description">
                                        <input
                                          type="text"
                                          value={editValue}
                                          onChange={(e) => setEditValue(e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') saveEditedDescription(permission.application_permission_id);
                                            if (e.key === 'Escape') cancelEditing();
                                          }}
                                          placeholder="Agregar descripción..."
                                          autoFocus
                                        />
                                        <button className="save-btn" onClick={() => saveEditedDescription(permission.application_permission_id)}>
                                          <HiOutlineCheck size={16} />
                                        </button>
                                        <button className="cancel-btn" onClick={cancelEditing}>
                                          <HiOutlineXMark size={16} />
                                        </button>
                                      </div>
                                    ) : (
                                      <span 
                                        className="permission-description clickable"
                                        onClick={() => startEditingDescription(permission)}
                                        title="Click para editar descripción"
                                      >
                                        {permission.application_permission_description || 'Sin descripción - click para agregar'}
                                      </span>
                                    )}
                                  </div>

                                  {/* Type Dropdown */}
                                  <div className="permission-type">
                                    <select
                                      value={permission.permission_type || ''}
                                      onChange={(e) => handleTypeChange(permission.application_permission_id, e.target.value)}
                                      className={permission.permission_type ? `type-${permission.permission_type}` : 'type-none'}
                                    >
                                      <option value="" disabled>Sin tipo</option>
                                      {PERMISSION_TYPES.map(type => (
                                        <option key={type.value} value={type.value}>
                                          {type.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  {/* Affect Money Toggle */}
                                  <button
                                    className={`affect-money-btn ${permission.affect_money ? 'active' : ''}`}
                                    onClick={() => handleToggleAffectMoney(permission.application_permission_id)}
                                    title={permission.affect_money ? 'Afecta dinero' : 'No afecta dinero'}
                                  >
                                    <HiOutlineCurrencyDollar size={18} />
                                  </button>

                                  {/* Delete */}
                                  <button
                                    className="delete-btn"
                                    onClick={() => handleDeletePermission(
                                      permission.application_permission_id,
                                      permission.application_permission_name
                                    )}
                                    title="Eliminar permiso"
                                  >
                                    <HiOutlineTrash size={18} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreatePermissionModal
          categories={categories}
          subcategories={subcategories}
          onClose={() => setShowCreateModal(false)}
          onCreated={(newPermission) => {
            setPermissions(prev => [...prev, newPermission]);
            setShowCreateModal(false);
            // Expand the category of the new permission
            setExpandedCategories(prev => new Set([...prev, newPermission.category]));
          }}
        />
      )}
    </div>
  );
};

// Create Permission Modal Component
interface CreatePermissionModalProps {
  categories: string[];
  subcategories: string[];
  onClose: () => void;
  onCreated: (permission: Permission) => void;
}

const CreatePermissionModal: React.FC<CreatePermissionModalProps> = ({
  categories,
  subcategories,
  onClose,
  onCreated
}) => {
  const [formData, setFormData] = useState({
    application_permission_name: '',
    application_permission_description: '',
    category: '',
    subcategory: '',
    permission_type: '',
    affect_money: false
  });
  const [loading, setLoading] = useState(false);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [showNewSubcategory, setShowNewSubcategory] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [newSubcategory, setNewSubcategory] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.application_permission_name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    const category = showNewCategory ? newCategory.trim() : formData.category;
    const subcategory = showNewSubcategory ? newSubcategory.trim() : formData.subcategory;
    
    if (!category) {
      toast.error('La categoría es requerida');
      return;
    }

    try {
      setLoading(true);
      const response = await CreateApplicationPermission({
        application_permission_name: formData.application_permission_name.trim(),
        application_permission_description: formData.application_permission_description.trim() || undefined,
        category,
        subcategory: subcategory || undefined,
        permission_type: formData.permission_type || undefined,
        affect_money: formData.affect_money
      });
      
      if (response.status === 201) {
        toast.success('Permiso creado exitosamente');
        onCreated(response.data);
      }
    } catch (error: any) {
      console.error('Error creating permission:', error);
      toast.error(error.response?.data?.message || 'Error al crear el permiso');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content create-permission-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Crear Nuevo Permiso</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Name */}
            <div className="form-group">
              <label>Nombre del Permiso *</label>
              <input
                type="text"
                value={formData.application_permission_name}
                onChange={(e) => setFormData({ ...formData, application_permission_name: e.target.value })}
                placeholder="Ej: Crear usuarios"
              />
            </div>

            {/* Description */}
            <div className="form-group">
              <label>Descripción</label>
              <textarea
                value={formData.application_permission_description}
                onChange={(e) => setFormData({ ...formData, application_permission_description: e.target.value })}
                placeholder="Descripción opcional del permiso"
                rows={3}
              />
            </div>

            {/* Category */}
            <div className="form-group">
              <label>Categoría *</label>
              {showNewCategory ? (
                <div className="input-with-toggle">
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="Nueva categoría"
                  />
                  <button
                    type="button"
                    className="toggle-btn"
                    onClick={() => setShowNewCategory(false)}
                  >
                    Seleccionar existente
                  </button>
                </div>
              ) : (
                <div className="input-with-toggle">
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="">Seleccionar categoría</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="toggle-btn"
                    onClick={() => setShowNewCategory(true)}
                  >
                    Crear nueva
                  </button>
                </div>
              )}
            </div>

            {/* Subcategory */}
            <div className="form-group">
              <label>Subcategoría</label>
              {showNewSubcategory ? (
                <div className="input-with-toggle">
                  <input
                    type="text"
                    value={newSubcategory}
                    onChange={(e) => setNewSubcategory(e.target.value)}
                    placeholder="Nueva subcategoría"
                  />
                  <button
                    type="button"
                    className="toggle-btn"
                    onClick={() => setShowNewSubcategory(false)}
                  >
                    Seleccionar existente
                  </button>
                </div>
              ) : (
                <div className="input-with-toggle">
                  <select
                    value={formData.subcategory}
                    onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                  >
                    <option value="">Sin subcategoría</option>
                    {subcategories.map(sub => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="toggle-btn"
                    onClick={() => setShowNewSubcategory(true)}
                  >
                    Crear nueva
                  </button>
                </div>
              )}
            </div>

            {/* Permission Type */}
            <div className="form-group">
              <label>Tipo de Permiso</label>
              <select
                value={formData.permission_type}
                onChange={(e) => setFormData({ ...formData, permission_type: e.target.value })}
              >
                <option value="">Sin tipo</option>
                {PERMISSION_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            {/* Affect Money */}
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.affect_money}
                  onChange={(e) => setFormData({ ...formData, affect_money: e.target.checked })}
                />
                <HiOutlineCurrencyDollar size={18} />
                <span>Este permiso afecta operaciones monetarias</span>
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Creando...' : 'Crear Permiso'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Permissions;
